import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import CreateUserComponent from '@/components/ClientsPage/CreateUser/CreateUserComponent';
import { useUser } from '@/contexts/UserContext';
import { useClientUsers } from '@/hooks/useClientUsers';
import { masksByCountry } from '@/helpers/mask';
import { showToast } from '@/store/ducks/toasts';


const CreateUserContainer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const intl = useIntl();
  const { id, status } = useParams();
  
  const { useClientFeatures, useCreateUserMutation } = useClientUsers(id);
  const { data: features = [], isLoading: loadingFeatures } = useClientFeatures();
  const createUser = useCreateUserMutation();

  const [user, setUser] = useState({ name: '', cpf: '', phone: '', username: '', email: '', features: [] });

  const me = useUser();
  const cpfMask = masksByCountry[me?.profile?.country || 'BR'].CPF_MASK;
  const cpfMaskPlaceholder = masksByCountry[me?.profile?.country || 'BR'].CPF_MASK_PLACEHOLDER;
  const phoneMask = masksByCountry[me?.profile?.country || 'BR'].PHONE_MASK;
  const phoneMaskPlaceholder = masksByCountry[me?.profile?.country || 'BR'].PHONE_MASK_PLACEHOLDER;
  const cellphoneMask = masksByCountry[me?.profile?.country || 'BR'].CELLPHONE_MASK;
  const cellphoneMaskLength = masksByCountry[me?.profile?.country || 'BR'].CELLPHONE_MASK_LENGTH;

  const onChange = evt => {
    const { name, value } = evt.target;
    setUser({ ...user, [name]: value });
  };

  const onSubmit = async evt => {
    evt.preventDefault();

    if (!user.features.length) {
      dispatch(
        showToast({
          type: 'error',
          title: intl.formatMessage({ id: 'ERROR' }),
          text: intl.formatMessage({ id: 'SELECT_AT_LEAST_ONE_PERMISSION_MESSAGE' }),
          duration: 5000,
        })
      );
      return;
    }

    createUser.mutate(user, {
      onSuccess: () => {
        navigate(`/clients/${status}/${id}`);
      },
      onError: (err) => {
        if (err.response && err.response.status < 500) {
          const firstKey = Object.keys(err.response.data)[0];
          const message = intl.formatMessage({ id: `${firstKey}.${err.response.data[firstKey]}` });
          dispatch(
            showToast({
              type: 'error',
              title: intl.formatMessage({ id: 'ERROR' }),
              text: message,
              duration: 5000,
            })
          );
        } else {
          dispatch(
            showToast({
              type: 'error',
              title: intl.formatMessage({ id: 'ERROR' }),
              text: intl.formatMessage({ id: 'USER_SAVE_ERROR' }),
              duration: 5000,
            })
          );
        }
      }
    });
  };

  const handleSelectedPermissionsChange = useCallback(
    event => {
      const value = Number(event.target.value);
      if (event.target.checked) {
        setUser(prev => ({
          ...prev,
          features: [...prev.features, value],
        }));
      } else {
        setUser(prev => ({
          ...prev,
          features: prev.features.filter(feature => feature !== value),
        }));
      }
    },
    []
  );

  useEffect(() => {
    if (features.length > 0) {
      setUser(prev => ({
        ...prev,
        features: features.filter(feature => feature.rule === 'opt-out').map(feature => feature.id),
      }));
    }
  }, [features]);

  const busy = createUser.isPending;

  return (
    <CreateUserComponent
      busy={busy}
      features={features}
      handleSelectedPermissionsChange={handleSelectedPermissionsChange}
      id={id}
      intl={intl}
      onChange={onChange}
      onSubmit={onSubmit}
      status={status}
      user={user}
      cpfMask={cpfMask}
      cpfMaskPlaceholder={cpfMaskPlaceholder}
      phoneMask={phoneMask}
      phoneMaskPlaceholder={phoneMaskPlaceholder}
      cellphoneMask={cellphoneMask}
      cellphoneMaskLength={cellphoneMaskLength}
    />
  );
};


export default CreateUserContainer;
